/** The React Query client: cache times, retry rules and the global error toasts. */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';

// Client factory
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only show error toasts for queries that are not in the background
        if (query.state.data !== undefined) {
          console.error('Query error:', error);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to fetch data. Please try again.',
            variant: 'destructive',
          });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        // The API interceptor (services/api-base.ts) owns the message for every failed
        // request, answered or not; a second toast here would only hide it.
        console.error('Mutation error:', error);
      },
    }),
    defaultOptions: {
      queries: {
        // Stale time - how long data is considered fresh
        staleTime: 5 * 60 * 1000, // 5 minutes
        
        // Cache time - how long data stays in cache after becoming unused
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        
        // Retry configuration
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors (client errors)
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        
        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        
        // Background refetch settings
        refetchOnWindowFocus: false, // switching tabs should not refetch
        refetchOnReconnect: true,    // Refetch when connection is restored
        refetchOnMount: true,        // Refetch when component mounts
        
        // Network mode
        networkMode: 'online',       // Only fetch when online
        
        // Suspense and error boundaries
        throwOnError: false,         // errors go to onError, not to error boundaries
      },
      mutations: {
        // Retry mutations once on network errors
        retry: (failureCount, error: any) => {
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          return failureCount < 1;
        },
        
        // Network mode for mutations
        networkMode: 'online',
      },
    },
  });
};

// Query key factories
export const queryKeys = {
  // Book management keys
  books: {
    all: ['books'] as const,
    lists: () => [...queryKeys.books.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.books.lists(), filters] as const,
    details: () => [...queryKeys.books.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.books.details(), id] as const,
    search: (query: string, filters?: Record<string, any>) => 
      [...queryKeys.books.all, 'search', query, filters] as const,
    availability: (id: number) => [...queryKeys.books.all, 'availability', id] as const,
    popular: (limit: number, category?: string) => 
      [...queryKeys.books.all, 'popular', limit, category] as const,
  },
  
  // Inventory keys
  inventory: {
    all: ['inventory'] as const,
    copies: () => [...queryKeys.inventory.all, 'copies'] as const,
    copy: (id: number) => [...queryKeys.inventory.copies(), id] as const,
    locations: () => [...queryKeys.inventory.all, 'locations'] as const,
    analytics: () => [...queryKeys.inventory.all, 'analytics'] as const,
  },
  
  // Loan keys
  loans: {
    all: ['loans'] as const,
    lists: () => [...queryKeys.loans.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.loans.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.loans.all, 'detail', id] as const,
    overdue: () => [...queryKeys.loans.all, 'overdue'] as const,
    user: (userId: number) => [...queryKeys.loans.all, 'user', userId] as const,
  },
  
  // Dashboard keys
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    analytics: (period: string) => [...queryKeys.dashboard.all, 'analytics', period] as const,
  },
  
  // User keys
  users: {
    all: ['users'] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
    profile: (id: number) => [...queryKeys.users.all, 'profile', id] as const,
  },
} as const;

// Cache invalidation utilities
export const cacheUtils = {
  // Invalidate all book-related queries
  invalidateBooks: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  },
  
  // Invalidate specific book
  invalidateBook: (queryClient: QueryClient, bookId: number) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.books.detail(bookId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.books.availability(bookId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.books.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
  },
  
  // Invalidate inventory
  invalidateInventory: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.books.all }); // Books depend on inventory
  },
  
  // Invalidate loans
  invalidateLoans: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.loans.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.books.all }); // Books depend on loan status
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  },
  
  // Optimistic updates for better UX
  updateBookInCache: (queryClient: QueryClient, bookId: number, updater: (old: any) => any) => {
    queryClient.setQueryData(queryKeys.books.detail(bookId), updater);
    
    // Update in lists as well
    queryClient.setQueriesData(
      { queryKey: queryKeys.books.lists() },
      (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((book: any) => 
            book.id === bookId ? updater(book) : book
          ),
        };
      }
    );
  },
  
  // Prefetch related data
  prefetchBookDetails: (queryClient: QueryClient, bookId: number) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.books.detail(bookId),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  },
  
  // Remove from cache
  removeBook: (queryClient: QueryClient, bookId: number) => {
    queryClient.removeQueries({ queryKey: queryKeys.books.detail(bookId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.books.lists() });
  },
};

// Performance monitoring utilities
export const performanceUtils = {
  // Log slow queries for monitoring
  logSlowQuery: (queryKey: readonly unknown[], duration: number) => {
    if (duration > 2000) { // Log queries taking more than 2 seconds
      console.warn(`Slow query detected:`, {
        queryKey,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });
    }
  },
  
  // Memory usage monitoring
  getCacheSize: (queryClient: QueryClient) => {
    const cache = queryClient.getQueryCache();
    return {
      queryCount: cache.getAll().length,
      // Note: Actual memory usage would require more sophisticated measurement
      timestamp: new Date().toISOString(),
    };
  },
  
  // Clear old cache entries
  clearStaleCache: (queryClient: QueryClient, maxAge: number = 30 * 60 * 1000) => {
    const cache = queryClient.getQueryCache();
    const now = Date.now();
    
    cache.getAll().forEach((query) => {
      if (query.state.dataUpdatedAt && (now - query.state.dataUpdatedAt) > maxAge) {
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    });
  },
};

// Default query client instance
export const queryClient = createOptimizedQueryClient();